import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Grid,
  Avatar,
  Chip,
  Stack,
  LinearProgress,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowForwardIos,
  ArrowBackIos,
  PlayArrow,
  Pause,
  Fullscreen,
  FullscreenExit,
  RocketLaunch,
  Speed,
  Security,
  Cloud,
  AccountBalance,
  LocalHospital,
  Business,
  ShoppingCart,
  LocalShipping,
  School,
  Assignment,
  DesignServices,
  Cable,
  CheckCircle,
  AutoAwesome,
  TrendingUp,
  Star,
  Close,
  Shield,
  SyncAlt,
  Storage,
  SwipeRightAlt,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import ThemeToggle from './ThemeToggle';

/* ─── helper: glassmorphism card sx ─── */
const glassCard = (isLight, borderColor) => ({
  p: { xs: 2, sm: 2.5, md: 3 },
  background: isLight ? 'rgba(255, 255, 255, 0.45)' : 'rgba(15, 23, 42, 0.5)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  border: isLight
    ? '1px solid rgba(255, 255, 255, 0.6)'
    : `1px solid ${borderColor || 'rgba(16, 185, 129, 0.25)'}`,
  borderRadius: 3,
  boxShadow: isLight ? '0 8px 32px rgba(99, 102, 241, 0.06)' : 'none',
});

function Presentation({
  open = false,
  onClose = () => {},
  initialSlide = 0,
  autoStart = false,
}) {
  const { isLight } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoStart);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [progress, setProgress] = useState(0);

  const containerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const touchStartRef = useRef(null);
  const touchDeltaRef = useRef(0);

  /* ─── Design tokens ─── */
  const accent = isLight ? '#6366f1' : '#10b981';
  const accentAlt = isLight ? '#4f46e5' : '#38bdf8';
  const textPrimary = isLight ? '#0f172a' : '#ffffff';
  const textSecondary = isLight
    ? 'rgba(15, 23, 42, 0.7)'
    : 'rgba(248, 250, 252, 0.7)';
  const textMuted = isLight
    ? 'rgba(15, 23, 42, 0.55)'
    : 'rgba(248, 250, 252, 0.55)';

  /* ─── Slides data ─── */
  const slides = [
    {
      id: 'hero',
      chip: 'ALPHANETIX',
      title: 'The Fastest Native No-Code Platform',
      content: {
        headline: 'Enterprise Backend. Native Speed. Zero Developers.',
        description:
          'The only no-code platform with native performance. Build complete, production-ready backend systems 10x faster — without writing a single line of code.',
        highlights: [
          { icon: <Speed />, text: 'Native Java Performance', color: accent },
          {
            icon: <Security />,
            text: 'Enterprise-Grade Architecture',
            color: accentAlt,
          },
          {
            icon: <RocketLaunch />,
            text: 'Production-Ready in Hours',
            color: isLight ? '#6366f1' : '#f59e0b',
          },
        ],
      },
    },
    {
      id: 'introduction',
      chip: 'THE COMPLETE SOLUTION',
      title: 'Beyond AI Code Generators',
      content: {
        headline: 'Where AI Tools Stop, We Deliver',
        description:
          'Copilot and Cursor generate code — but you still need developers to compile, deploy, and architect. We deliver production-ready systems with enterprise architecture built-in.',
        metrics: [
          { value: '10x', label: 'Faster to Production', progress: 95 },
          { value: 'Zero', label: 'Code to Maintain', progress: 100 },
          { value: '100%', label: 'Architecture Coverage', progress: 98 },
          { value: 'Instant', label: 'Deployment', progress: 100 },
        ],
      },
    },
    {
      id: 'advantages',
      chip: 'UNFAIR ADVANTAGE',
      title: 'Native Speed Meets No-Code Simplicity',
      content: {
        headline: 'What Others Cannot Replicate',
        advantages: [
          {
            title: 'True Native Performance',
            subtitle: '10x Faster Than Competitors',
            description:
              'Other no-code platforms interpret at runtime. We compile to native Java — delivering speed that rivals hand-coded solutions.',
            icon: <Speed />,
            color: isLight ? '#4f46e5' : '#0ea5e9',
          },
          {
            title: 'Enterprise Architecture Built-In',
            subtitle: 'Security • Caching • Monitoring',
            description:
              'Authentication, authorization, logging, error handling — state-of-the-art cross-cutting concerns pre-engineered into every workflow.',
            icon: <Shield />,
            color: isLight ? '#6366f1' : '#10b981',
          },
          {
            title: 'AI-Native Platform',
            subtitle: 'Intelligence Everywhere',
            description:
              "AI doesn't just suggest — it designs workflows, optimizes logic, and continuously improves. Embedded at every layer.",
            icon: <AutoAwesome />,
            color: '#8b5cf6',
          },
          {
            title: 'Zero Learning Curve',
            subtitle: 'Business Users Ship Products',
            description:
              'No prompt engineering. No architecture expertise. The platform knows enterprise patterns — you just describe what you need.',
            icon: <TrendingUp />,
            color: isLight ? '#6366f1' : '#f59e0b',
          },
        ],
      },
    },
    {
      id: 'industry',
      chip: 'MARKET OPPORTUNITY',
      title: '$200B+ Addressable Market',
      content: {
        headline: 'Six Enterprise Verticals Ready for Disruption',
        industries: [
          {
            title: 'FinTech',
            icon: <AccountBalance />,
            description:
              'Transaction processing, compliance automation, and real-time fraud detection.',
            color: isLight ? '#6366f1' : '#10b981',
            useCases: ['Digital Payments', 'Trading Platforms', 'Banking APIs'],
          },
          {
            title: 'Healthcare',
            icon: <LocalHospital />,
            description:
              'HIPAA-compliant integration with automated patient workflows and EMR sync.',
            color: '#047857',
            useCases: ['EHR Integration', 'Telemedicine', 'Lab Systems'],
          },
          {
            title: 'Enterprise',
            icon: <Business />,
            description:
              'Unified CRM-ERP systems, supply chain automation, and advanced analytics.',
            color: isLight ? '#4f46e5' : '#0ea5e9',
            useCases: ['CRM Integration', 'ERP Systems', 'Business Intelligence'],
          },
          {
            title: 'E-Commerce',
            icon: <ShoppingCart />,
            description:
              'Inventory sync, multi-channel order management, and fulfillment automation.',
            color: isLight ? '#4f46e5' : '#f59e0b',
            useCases: ['Order Management', 'Inventory Sync', 'Marketplace Integration'],
          },
          {
            title: 'Logistics',
            icon: <LocalShipping />,
            description:
              'Fleet tracking, route optimization, and end-to-end supply chain visibility.',
            color: isLight ? '#0369a1' : '#38bdf8',
            useCases: ['Fleet Management', 'Route Optimization', 'Shipment Tracking'],
          },
          {
            title: 'EdTech',
            icon: <School />,
            description:
              'Learning platform integration, student data management, and content delivery.',
            color: isLight ? '#3730a3' : '#fb923c',
            useCases: ['LMS Integration', 'Assessment Platforms', 'Content Delivery'],
          },
        ],
      },
    },
    {
      id: 'deployment',
      chip: 'GO-TO-MARKET',
      title: 'Flexible Revenue Models',
      content: {
        headline: 'Every Customer Segment. Maximum Lifetime Value.',
        options: [
          {
            title: 'Fully Managed Cloud',
            subtitle: 'Scalable Recurring Revenue',
            icon: <Cloud />,
            description:
              'Complete SaaS experience. We manage everything — customers focus on building workflows.',
            features: [
              'Zero Infrastructure Overhead',
              'Pay-As-You-Grow',
              'Continuous Updates',
            ],
            color: isLight ? '#6366f1' : '#10b981',
          },
          {
            title: 'Hybrid Deployment',
            subtitle: 'Best of Both Worlds',
            icon: <SyncAlt />,
            description:
              'Data stays on-premises, platform runs in our cloud. Or selective modules deployed locally for ultra-low latency.',
            features: [
              'Data Sovereignty + Managed Platform',
              'Edge Processing',
              'Seamless Sync',
            ],
            color: '#8b5cf6',
          },
          {
            title: 'Full On-Premises',
            subtitle: 'Premium Enterprise Contracts',
            icon: <Storage />,
            description:
              'Complete deployment within customer infrastructure. For highly regulated industries with strict compliance.',
            features: [
              'Total Data Control',
              'Air-Gapped Option',
              'White-Glove Support',
            ],
            color: isLight ? '#818cf8' : '#e11d48',
          },
        ],
      },
    },
    {
      id: 'process',
      chip: 'HOW IT WORKS',
      title: 'Idea to Production in Hours',
      content: {
        headline: 'Four Steps. No Developers. Full Backend.',
        steps: [
          {
            label: 'Describe',
            title: 'State Your Need',
            icon: <Assignment />,
            description:
              'Describe your business process in plain language. AI translates it into workflow design.',
            time: '5 Min',
            color: accent,
          },
          {
            label: 'Design',
            title: 'AI-Assisted Builder',
            icon: <DesignServices />,
            description:
              'Visual drag-and-drop with intelligent suggestions. No coding, no complex prompts.',
            time: '1-2 Hrs',
            color: '#047857',
          },
          {
            label: 'Connect',
            title: 'Instant Integrations',
            icon: <Cable />,
            description: '1000+ pre-built connectors.',
            time: '10 Min',
            color: '#38bdf8',
          },
          {
            label: 'Run',
            title: 'Production Live',
            icon: <RocketLaunch />,
            description:
              'One click to deploy. Native performance. Enterprise monitoring. Done.',
            time: 'Instant',
            color: '#c084fc',
          },
        ],
      },
    },
    {
      id: 'cta',
      chip: 'THE FUTURE IS HERE',
      title: 'Native Speed. Zero Code. Infinite Scale.',
      content: {
        headline: 'Ready to Revolutionize How Software Gets Built?',
        description:
          'Stop choosing between speed and simplicity. Alphanetix delivers both — native performance with no-code ease. The complete backend platform that makes developers optional.',
        stats: [
          {
            value: '10x',
            label: 'Faster Delivery',
            color: isLight ? '#4f46e5' : '#0ea5e9',
          },
          {
            value: '90%',
            label: 'Cost Reduction',
            color: isLight ? '#6366f1' : '#10b981',
          },
          { value: '99.9%', label: 'Uptime SLA', color: '#8b5cf6' },
          { value: '∞', label: 'Scalability', color: isLight ? '#6366f1' : '#f59e0b' },
        ],
        benefits: [
          'Native Performance Engine',
          'AI-Powered Design & Optimization',
          'Enterprise Architecture Built-In',
          '1000+ Pre-Built Connectors',
        ],
      },
    },
  ];

  // Lock body scroll while presentation is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Initialize
  useEffect(() => {
    if (open) {
      setCurrentSlide(initialSlide);
      setIsAutoPlaying(autoStart);
    }
  }, [open, initialSlide, autoStart]);

  // Auto-play
  useEffect(() => {
    if (isAutoPlaying && open) {
      progressIntervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            setCurrentSlide((s) => (s + 1) % slides.length);
            return 0;
          }
          return prev + 2;
        });
      }, 250);
    } else {
      if (progressIntervalRef.current)
        clearInterval(progressIntervalRef.current);
      setProgress(0);
    }
    return () => {
      if (progressIntervalRef.current)
        clearInterval(progressIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoPlaying, open]);

  // Fullscreen
  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (!open) return;
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          previousSlide();
          break;
        case 'Escape':
          e.preventDefault();
          if (isFullscreen) document.exitFullscreen?.();
          else onClose();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
        default:
          break;
      }
    };
    if (open) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isFullscreen]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setProgress(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length]);

  const previousSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setProgress(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setProgress(0);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying((a) => !a);
    setProgress(0);
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement)
        await containerRef.current?.requestFullscreen();
      else await document.exitFullscreen();
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  /* ─── Touch / swipe support ─── */
  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
    touchDeltaRef.current = 0;
  };
  const handleTouchMove = (e) => {
    if (touchStartRef.current == null) return;
    touchDeltaRef.current = e.touches[0].clientX - touchStartRef.current;
  };
  const handleTouchEnd = () => {
    if (Math.abs(touchDeltaRef.current) > 60) {
      if (touchDeltaRef.current < 0) nextSlide();
      else previousSlide();
    }
    touchStartRef.current = null;
    touchDeltaRef.current = 0;
  };

  /* ════════════════════════════════════════════
     SLIDE RENDERERS
     ════════════════════════════════════════════ */

  const renderHero = (slide) => (
    <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
      <Grid item xs={12} md={7}>
        <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: {
                xs: '1.6rem',
                sm: '2.2rem',
                md: '3rem',
                lg: '3.5rem',
              },
              fontWeight: 900,
              lineHeight: 1.05,
              mb: { xs: 2, md: 3 },
              color: textPrimary,
            }}
          >
            THE FASTEST
            <br />
            <Box
              component="span"
              sx={{
                background: isLight
                  ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #38bdf8 100%)'
                  : 'linear-gradient(135deg, #38bdf8 0%, #8b5cf6 50%, #c084fc 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              NATIVE NO-CODE
            </Box>
            <br />
            PLATFORM
          </Typography>
          <Typography
            sx={{
              mb: { xs: 2, md: 3 },
              color: textSecondary,
              fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.1rem' },
              lineHeight: 1.6,
            }}
          >
            {slide.content.description}
          </Typography>
          <Stack spacing={1.5}>
            {slide.content.highlights.map((h, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}
              >
                <Avatar
                  sx={{
                    width: { xs: 32, md: 40 },
                    height: { xs: 32, md: 40 },
                    bgcolor: `${h.color}15`,
                    color: h.color,
                    border: `1.5px solid ${h.color}30`,
                  }}
                >
                  {React.cloneElement(h.icon, {
                    sx: { fontSize: { xs: '1rem', md: '1.2rem' } },
                  })}
                </Avatar>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.85rem', md: '1rem' },
                    color: textPrimary,
                  }}
                >
                  {h.text}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Grid>
      <Grid item xs={12} md={5}>
        <Paper
          elevation={0}
          sx={{
            ...glassCard(isLight, `${accentAlt}30`),
            textAlign: 'center',
            p: { xs: 3, md: 4 },
          }}
        >
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              background: isLight
                ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                : 'linear-gradient(135deg, #38bdf8, #10b981)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            10x
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: textPrimary,
              mb: 0.5,
              fontSize: { xs: '0.95rem', md: '1.1rem' },
            }}
          >
            FASTER TO PRODUCTION
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: textMuted,
              fontSize: { xs: '0.8rem', md: '0.85rem' },
            }}
          >
            Native speed. Zero developers.
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderIntroduction = (slide) => (
    <Box sx={{ textAlign: 'center', width: '100%' }}>
      <Typography
        sx={{
          mb: { xs: 1.5, md: 3 },
          fontWeight: 700,
          fontSize: { xs: '1.1rem', sm: '1.4rem', md: '1.75rem' },
          color: textPrimary,
        }}
      >
        {slide.content.headline}
      </Typography>
      <Typography
        sx={{
          mb: { xs: 3, md: 5 },
          color: textSecondary,
          maxWidth: 700,
          mx: 'auto',
          fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
          lineHeight: 1.6,
        }}
      >
        {slide.content.description}
      </Typography>
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {slide.content.metrics.map((m, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Paper
              elevation={0}
              sx={{
                ...glassCard(isLight, `${accent}30`),
                textAlign: 'center',
                height: '100%',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: {
                    xs: '1.5rem',
                    sm: '1.8rem',
                    md: '2.2rem',
                  },
                  background: isLight
                    ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                    : 'linear-gradient(135deg, #10b981, #38bdf8)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5,
                }}
              >
                {m.value}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                  color: textPrimary,
                  mb: 1,
                }}
              >
                {m.label}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={m.progress}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  bgcolor: `${accent}20`,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: accent,
                    borderRadius: 2,
                  },
                }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderAdvantages = (slide) => (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          textAlign: 'center',
          mb: { xs: 2, md: 4 },
          fontWeight: 700,
          fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.75rem' },
          color: textPrimary,
        }}
      >
        {slide.content.headline}
      </Typography>
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {slide.content.advantages.map((adv, i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Paper
              elevation={0}
              sx={{
                ...glassCard(isLight, `${adv.color}30`),
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: isMobile ? 'none' : 'translateY(-4px)',
                  borderColor: `${adv.color}50`,
                  boxShadow: `0 12px 32px ${adv.color}15`,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1.5,
                  gap: 1.5,
                }}
              >
                <Avatar
                  sx={{
                    width: { xs: 36, md: 44 },
                    height: { xs: 36, md: 44 },
                    bgcolor: `${adv.color}12`,
                    color: adv.color,
                    border: `1.5px solid ${adv.color}25`,
                  }}
                >
                  {React.cloneElement(adv.icon, {
                    sx: { fontSize: { xs: '1.1rem', md: '1.4rem' } },
                  })}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: adv.color,
                      fontWeight: 700,
                      fontSize: { xs: '0.6rem', md: '0.7rem' },
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {adv.subtitle}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: textPrimary,
                      fontSize: { xs: '0.85rem', md: '1rem' },
                      lineHeight: 1.3,
                    }}
                  >
                    {adv.title}
                  </Typography>
                </Box>
              </Box>
              <Typography
                sx={{
                  color: textSecondary,
                  fontSize: { xs: '0.75rem', md: '0.85rem' },
                  lineHeight: 1.5,
                }}
              >
                {adv.description}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderIndustry = (slide) => (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          textAlign: 'center',
          mb: { xs: 2, md: 4 },
          fontWeight: 700,
          fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.75rem' },
          color: textPrimary,
        }}
      >
        {slide.content.headline}
      </Typography>
      <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
        {slide.content.industries.map((ind, i) => (
          <Grid item xs={6} sm={4} md={4} key={i}>
            <Paper
              elevation={0}
              sx={{
                ...glassCard(isLight, `${ind.color}30`),
                p: { xs: 1.5, sm: 2, md: 2.5 },
                textAlign: 'center',
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: isMobile ? 'none' : 'translateY(-4px)',
                  borderColor: `${ind.color}50`,
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: `${ind.color}15`,
                  color: ind.color,
                  width: { xs: 36, sm: 44, md: 52 },
                  height: { xs: 36, sm: 44, md: 52 },
                  mx: 'auto',
                  mb: 1,
                  border: `1.5px solid ${ind.color}25`,
                }}
              >
                {React.cloneElement(ind.icon, {
                  sx: { fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.6rem' } },
                })}
              </Avatar>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: textPrimary,
                  mb: 0.5,
                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                }}
              >
                {ind.title}
              </Typography>
              <Typography
                sx={{
                  color: textSecondary,
                  mb: 1.5,
                  fontSize: { xs: '0.65rem', sm: '0.72rem', md: '0.8rem' },
                  lineHeight: 1.4,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {ind.description}
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                justifyContent="center"
                flexWrap="wrap"
                useFlexGap
              >
                {ind.useCases.map((uc, j) => (
                  <Chip
                    key={j}
                    label={uc}
                    size="small"
                    sx={{
                      bgcolor: `${ind.color}18`,
                      color: ind.color,
                      fontWeight: 700,
                      fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.68rem' },
                      height: { xs: 20, sm: 22, md: 24 },
                      border: `1px solid ${ind.color}30`,
                      mb: 0.5,
                    }}
                  />
                ))}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderDeployment = (slide) => (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          textAlign: 'center',
          mb: { xs: 2, md: 4 },
          fontWeight: 700,
          fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.75rem' },
          color: textPrimary,
        }}
      >
        {slide.content.headline}
      </Typography>
      <Grid
        container
        spacing={{ xs: 1.5, sm: 2, md: 3 }}
        justifyContent="center"
      >
        {slide.content.options.map((opt, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Paper
              elevation={0}
              sx={{
                ...glassCard(isLight, `${opt.color}30`),
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: isMobile ? 'none' : 'translateY(-4px)',
                  borderColor: `${opt.color}50`,
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: `${opt.color}15`,
                  color: opt.color,
                  width: { xs: 44, md: 56 },
                  height: { xs: 44, md: 56 },
                  mx: 'auto',
                  mb: 1.5,
                  border: `1.5px solid ${opt.color}25`,
                }}
              >
                {React.cloneElement(opt.icon, {
                  sx: { fontSize: { xs: '1.3rem', md: '1.8rem' } },
                })}
              </Avatar>
              <Typography
                sx={{
                  color: opt.color,
                  fontWeight: 700,
                  fontSize: { xs: '0.6rem', md: '0.7rem' },
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 0.25,
                }}
              >
                {opt.subtitle}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: textPrimary,
                  mb: 1,
                  fontSize: { xs: '0.9rem', md: '1.05rem' },
                }}
              >
                {opt.title}
              </Typography>
              <Typography
                sx={{
                  color: textSecondary,
                  mb: 2,
                  fontSize: { xs: '0.75rem', md: '0.8rem' },
                  lineHeight: 1.5,
                  flex: 1,
                }}
              >
                {opt.description}
              </Typography>
              <Stack spacing={1} alignItems="flex-start" sx={{ textAlign: 'left' }}>
                {opt.features.map((f, j) => (
                  <Box
                    key={j}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <CheckCircle
                      sx={{
                        color: opt.color,
                        fontSize: { xs: '0.85rem', md: '1rem' },
                      }}
                    />
                    <Typography
                      sx={{
                        color: textSecondary,
                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                      }}
                    >
                      {f}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderProcess = (slide) => (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          textAlign: 'center',
          mb: { xs: 2, md: 4 },
          fontWeight: 700,
          fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.75rem' },
          color: textPrimary,
        }}
      >
        {slide.content.headline}
      </Typography>
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {slide.content.steps.map((step, i) => (
          <Grid item xs={6} sm={6} md={3} key={i}>
            <Paper
              elevation={0}
              sx={{
                ...glassCard(isLight, `${step.color}30`),
                textAlign: 'center',
                height: '100%',
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: isMobile ? 'none' : 'translateY(-4px)',
                  borderColor: `${step.color}50`,
                },
              }}
            >
              {/* Step number */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 10,
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  color: step.color,
                  opacity: 0.4,
                }}
              >
                0{i + 1}
              </Box>
              <Avatar
                sx={{
                  bgcolor: `${step.color}15`,
                  color: step.color,
                  width: { xs: 40, md: 52 },
                  height: { xs: 40, md: 52 },
                  mx: 'auto',
                  mb: 1,
                  border: `1.5px solid ${step.color}25`,
                }}
              >
                {React.cloneElement(step.icon, {
                  sx: { fontSize: { xs: '1.2rem', md: '1.6rem' } },
                })}
              </Avatar>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: textPrimary,
                  fontSize: { xs: '0.85rem', md: '1rem' },
                  mb: 0.25,
                }}
              >
                {step.label}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: textPrimary,
                  fontSize: { xs: '0.7rem', md: '0.8rem' },
                  mb: 1,
                  opacity: 0.8,
                }}
              >
                {step.title}
              </Typography>
              <Typography
                sx={{
                  color: textSecondary,
                  fontSize: { xs: '0.65rem', md: '0.78rem' },
                  lineHeight: 1.5,
                  mb: 1.5,
                }}
              >
                {step.description}
              </Typography>
              <Chip
                label={step.time}
                size="small"
                sx={{
                  bgcolor: `${step.color}18`,
                  color: step.color,
                  fontWeight: 700,
                  fontSize: { xs: '0.6rem', md: '0.7rem' },
                  height: { xs: 22, md: 26 },
                  border: `1px solid ${step.color}30`,
                }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderCTA = (slide) => (
    <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
      <Grid item xs={12} md={6}>
        <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
          <Typography
            sx={{
              mb: { xs: 1.5, md: 3 },
              fontWeight: 700,
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.75rem' },
              color: textPrimary,
            }}
          >
            {slide.content.headline}
          </Typography>
          <Typography
            sx={{
              mb: { xs: 2, md: 3 },
              color: textSecondary,
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
              lineHeight: 1.6,
            }}
          >
            {slide.content.description}
          </Typography>
          <Stack spacing={1} sx={{ mb: { xs: 2, md: 3 } }}>
            {slide.content.benefits.map((b, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}
              >
                <CheckCircle
                  sx={{
                    color: accent,
                    fontSize: { xs: '0.9rem', md: '1.1rem' },
                  }}
                />
                <Typography
                  sx={{
                    color: textPrimary,
                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                  }}
                >
                  {b}
                </Typography>
              </Box>
            ))}
          </Stack>
          <Button
            variant="contained"
            size={isMobile ? 'medium' : 'large'}
            endIcon={<RocketLaunch />}
            sx={{
              py: { xs: 1.25, md: 1.75 },
              px: { xs: 3, md: 4 },
              fontSize: { xs: '0.85rem', md: '1rem' },
              fontWeight: 700,
              borderRadius: '12px',
              background: isLight
                ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              textTransform: 'none',
              boxShadow: isLight
                ? '0 8px 24px rgba(99, 102, 241, 0.3)'
                : '0 8px 24px rgba(16, 185, 129, 0.3)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: isLight
                  ? '0 12px 32px rgba(99, 102, 241, 0.4)'
                  : '0 12px 32px rgba(16, 185, 129, 0.4)',
              },
            }}
          >
            Request Demo Today
          </Button>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Grid container spacing={{ xs: 1.5, md: 2 }}>
          {slide.content.stats.map((s, i) => (
            <Grid item xs={6} key={i}>
              <Paper
                elevation={0}
                sx={{
                  ...glassCard(isLight, `${s.color}30`),
                  textAlign: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '1.6rem', sm: '2rem', md: '2.5rem' },
                    color: s.color,
                    mb: 0.5,
                  }}
                >
                  {s.value}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: textPrimary,
                    fontSize: { xs: '0.7rem', md: '0.85rem' },
                  }}
                >
                  {s.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );

  const renderSlideContent = (slide) => {
    switch (slide.id) {
      case 'hero':
        return renderHero(slide);
      case 'introduction':
        return renderIntroduction(slide);
      case 'advantages':
        return renderAdvantages(slide);
      case 'industry':
        return renderIndustry(slide);
      case 'deployment':
        return renderDeployment(slide);
      case 'process':
        return renderProcess(slide);
      case 'cta':
        return renderCTA(slide);
      default:
        return null;
    }
  };

  if (!open) return null;

  const slide = slides[currentSlide];

  /* ─── Control button shared sx ─── */
  const ctrlBtn = {
    bgcolor: isLight
      ? 'rgba(255, 255, 255, 0.7)'
      : 'rgba(15, 23, 42, 0.7)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    color: textPrimary,
    border: isLight
      ? '1px solid rgba(255,255,255,0.8)'
      : '1px solid rgba(255,255,255,0.08)',
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: isLight
        ? 'rgba(255, 255, 255, 0.9)'
        : 'rgba(15, 23, 42, 0.9)',
      transform: 'scale(1.08)',
    },
  };

  return (
    <Box
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: isLight
          ? 'linear-gradient(145deg, #f0f2ff 0%, #f8f9ff 50%, #ffffff 100%)'
          : 'linear-gradient(135deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // Restore native cursor in fullscreen (custom cursor overlay is outside
        // the fullscreen element so it's not visible in fullscreen mode)
        ...(isFullscreen && {
          cursor: 'default',
          '& *': { cursor: 'inherit' },
          '& button, & a, & [role="button"]': { cursor: 'pointer' },
        }),
      }}
    >
      {/* ─── Auto-play progress ─── */}
      {isAutoPlaying && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 20,
            height: 3,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: isLight
                ? 'linear-gradient(90deg, #6366f1, #4f46e5)'
                : 'linear-gradient(90deg, #10b981, #38bdf8)',
            },
          }}
        />
      )}

      {/* ─── Top controls ─── */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 8, md: 16 },
          right: { xs: 8, md: 20 },
          zIndex: 20,
          display: 'flex',
          gap: { xs: 0.5, md: 1 },
        }}
      >
        <ThemeToggle
          sx={{
            ...ctrlBtn,
            width: { xs: 36, md: 40 },
            height: { xs: 36, md: 40 },
          }}
        />
        <IconButton
          onClick={toggleAutoPlay}
          size={isMobile ? 'small' : 'medium'}
          sx={ctrlBtn}
        >
          {isAutoPlaying ? (
            <Pause fontSize="small" />
          ) : (
            <PlayArrow fontSize="small" />
          )}
        </IconButton>
        {!isMobile && (
          <IconButton onClick={toggleFullscreen} sx={ctrlBtn}>
            {isFullscreen ? (
              <FullscreenExit fontSize="small" />
            ) : (
              <Fullscreen fontSize="small" />
            )}
          </IconButton>
        )}
        <IconButton
          onClick={onClose}
          size={isMobile ? 'small' : 'medium'}
          sx={ctrlBtn}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* ─── Slide counter (top-left) ─── */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 12, md: 20 },
          left: { xs: 12, md: 24 },
          zIndex: 20,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '0.7rem', md: '0.8rem' },
            fontWeight: 700,
            color: textMuted,
            letterSpacing: '1px',
            fontFamily: 'monospace',
          }}
        >
          {String(currentSlide + 1).padStart(2, '0')} /{' '}
          {String(slides.length).padStart(2, '0')}
        </Typography>
      </Box>

      {/* ─── Main scrollable content area ─── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          pt: { xs: '52px', md: '70px' },
          pb: { xs: '80px', md: '90px' },
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Fade in={true} timeout={500} key={currentSlide}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: { xs: 'flex-start', md: 'center' },
              maxWidth: 1200,
              width: '100%',
              mx: 'auto',
            }}
          >
            {/* ─── Slide header ─── */}
            <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 4 } }}>
              <Chip
                icon={<Star sx={{ fontSize: '0.9rem' }} />}
                label={slide.chip}
                sx={{
                  mb: 1.5,
                  px: { xs: 1.5, md: 2.5 },
                  height: { xs: 28, md: 32 },
                  background: isLight
                    ? 'rgba(255, 255, 255, 0.6)'
                    : 'rgba(16, 185, 129, 0.12)',
                  backdropFilter: 'blur(12px)',
                  border: isLight
                    ? '1px solid rgba(255, 255, 255, 0.8)'
                    : '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '50px',
                  color: isLight ? accent : '#ffffff',
                  fontSize: { xs: '0.6rem', md: '0.75rem' },
                  fontWeight: 700,
                  letterSpacing: '1px',
                  '& .MuiChip-icon': { color: accent },
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  fontSize: {
                    xs: '1.3rem',
                    sm: '1.8rem',
                    md: '2.5rem',
                  },
                  color: textPrimary,
                  lineHeight: 1.15,
                }}
              >
                {slide.title}
              </Typography>
            </Box>

            {/* ─── Slide content ─── */}
            <Box sx={{ width: '100%' }}>{renderSlideContent(slide)}</Box>
          </Box>
        </Fade>
      </Box>

      {/* ─── Bottom navigation bar ─── */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'space-between',
          px: { xs: 1.5, md: 3 },
          py: { xs: 1.5, md: 2 },
          background: isLight
            ? 'linear-gradient(to top, rgba(240,242,255,0.95), rgba(240,242,255,0.6), transparent)'
            : 'linear-gradient(to top, rgba(2,6,23,0.95), rgba(2,6,23,0.6), transparent)',
        }}
      >
        {/* Left arrow (desktop only) */}
        {!isMobile && (
          <IconButton
            onClick={previousSlide}
            sx={{
              ...ctrlBtn,
              width: 48,
              height: 48,
            }}
          >
            <ArrowBackIos sx={{ fontSize: '1.2rem', ml: '4px' }} />
          </IconButton>
        )}

        {/* Slide indicators */}
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 0.5, md: 0.75 },
            alignItems: 'center',
          }}
        >
          {slides.map((_, idx) => (
            <Box
              key={idx}
              onClick={() => goToSlide(idx)}
              sx={{
                width:
                  idx === currentSlide
                    ? { xs: 24, md: 32 }
                    : { xs: 8, md: 10 },
                height: { xs: 8, md: 10 },
                borderRadius: '5px',
                bgcolor:
                  idx === currentSlide
                    ? accent
                    : isLight
                    ? 'rgba(15, 23, 42, 0.18)'
                    : 'rgba(248, 250, 252, 0.2)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor:
                    idx === currentSlide
                      ? accent
                      : isLight
                      ? 'rgba(15, 23, 42, 0.35)'
                      : 'rgba(248, 250, 252, 0.4)',
                },
              }}
            />
          ))}
        </Box>

        {/* Right arrow (desktop only) */}
        {!isMobile && (
          <IconButton
            onClick={nextSlide}
            sx={{
              ...ctrlBtn,
              width: 48,
              height: 48,
            }}
          >
            <ArrowForwardIos sx={{ fontSize: '1.2rem' }} />
          </IconButton>
        )}
      </Box>

      {/* ─── Mobile swipe hint ─── */}
      {isMobile && (
        <Fade in={currentSlide === 0} timeout={{ enter: 1200, exit: 800 }}>
          <Box
            sx={{
              position: 'absolute',
              bottom: 60,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2,
              py: 0.75,
              borderRadius: '20px',
              bgcolor: isLight
                ? 'rgba(15, 23, 42, 0.06)'
                : 'rgba(248, 250, 252, 0.06)',
              backdropFilter: 'blur(8px)',
              zIndex: 15,
              pointerEvents: 'none',
            }}
          >
            <SwipeRightAlt
              sx={{
                fontSize: '1.1rem',
                color: accent,
                animation: 'swipeHint 2s ease-in-out infinite',
                '@keyframes swipeHint': {
                  '0%, 100%': { transform: 'translateX(0)' },
                  '50%': { transform: 'translateX(6px)' },
                },
              }}
            />
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: textMuted,
                fontWeight: 600,
                letterSpacing: '0.03em',
              }}
            >
              Swipe to navigate
            </Typography>
          </Box>
        </Fade>
      )}
    </Box>
  );
}

export default Presentation;
